import * as Highcharts from 'highcharts'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../styled'
import { defaultTooltipPadding } from '../../design-system'

export { Chart } from './chart'

export const chartTooltipHeaderTitleCss = `font-weight: ${deprecatedTheme.fontWeights.primary.bold};`

export const sharedTooltipPadding = 22

export const getChartTooltipContainerCss = (padding?: number) =>
  `padding: ${
    !!padding ? padding : defaultTooltipPadding
  }px; line-height: 1.4; font-family: ${
    deprecatedTheme.fontFamilies.secondary
  }; font-size: 12px; color: ${theme.color.white};`

export const SERIES_LINE_WIDTH = 1.2
export const SERIES_LINE_WIDTH_ON_HOVER = 2
export const BG_SERIES_LINE_WIDTH = 0
export const BG_SERIES_LINE_WIDTH_ON_HOVER = 0.5

export const MARKER_LINE_WIDTH = 3
export const MARKER_RADIUS = 4

export const PLOT_LINE_WIDTH = 1

export const linearGradient: Highcharts.LinearGradientColorObject = {
  x1: 0,
  y1: 1,
  x2: 0,
  y2: 0
}

interface ChartSeriesProps {
  zIndex: number
  id: string
  lineColor: Highcharts.ColorString
  areaColor: Highcharts.GradientColorObject | Highcharts.ColorString
  areaColorBg: Highcharts.ColorString
}

export type ChartSeriesConfig<TSeriesType extends string> = Record<
  TSeriesType,
  ChartSeriesProps
>

export const setArrowForSharedTooltip = () => {
  // https://stackoverflow.com/a/48322353/7685417
  Highcharts.wrap(
    Highcharts.Tooltip.prototype,
    'move',
    // @ts-ignore TypeScript callback function of WrapProceedFunction is being overridden with this workaround
    function (_proceed, x, y, anchorX, anchorY) {
      // "this" represents the tooltip object
      let now = this.now
      const animate =
        this.options.animation !== false &&
        !this.isHidden &&
        // When we get close to the target position, abort animation and
        // land on the right place (#3056)
        (Math.abs(x - now.x) > 1 || Math.abs(y - now.y) > 1)
      const skipAnchor = false // tooltip.followPointer || tooltip.len > 1

      // Get intermediate values for animation
      now = Object.assign(now, {
        x: animate ? (2 * now.x + x) / 3 : x,
        y: animate ? (now.y + y) / 2 : y,
        anchorX: skipAnchor
          ? undefined
          : animate
          ? (2 * now.anchorX + anchorX) / 3
          : anchorX,
        anchorY: skipAnchor
          ? undefined
          : animate
          ? (now.anchorY + anchorY) / 2
          : anchorY
      })

      // Move to the intermediate value
      this.getLabel().attr(now)

      // Run on next tick of the mouse tracker
      if (animate) {
        // Never allow two timeouts
        clearTimeout(this.tooltipTimeout)

        // Set the fixed interval ticking for the smooth tooltip
        this.tooltipTimeout = setTimeout(() => {
          // The interval function may still be running during destroy,
          // so check that the chart is really there before calling.
          if (this) {
            this.move(x, y, anchorX, anchorY)
          }
        }, 32)
      }
    }
  )
}

const addClickEventToChartSeries = (
  seriesOptions: Highcharts.SeriesAreasplineOptions,
  onClick?: () => void
): Highcharts.SeriesAreasplineOptions => {
  seriesOptions.events = {
    click() {
      if (!!onClick) {
        onClick()
      }
    }
  }
  return seriesOptions
}

export const buildCrosshair = (
  zIndex: number
): Highcharts.AxisCrosshairOptions => {
  return {
    zIndex,
    color: theme.color.neutral80,
    width: 1.3
  }
}

export const buildYAxisPlotLinesOptions = (
  value: number,
  zIndex: number
): Highcharts.YAxisPlotLinesOptions => {
  return {
    color: theme.color.neutral50,
    width: PLOT_LINE_WIDTH,
    dashStyle: 'Dash',
    zIndex,
    value
  }
}

export const buildChartSeriesOptions = (
  values: number[],
  timeline: number[],
  id: string,
  lineColor:
    | Highcharts.ColorString
    | Highcharts.GradientColorObject
    | Highcharts.PatternObject,
  areaColor:
    | Highcharts.ColorString
    | Highcharts.GradientColorObject
    | Highcharts.PatternObject,
  zIndex: number,
  cursor: string | undefined,
  enabled: boolean,
  processZeroValueZones: boolean,
  onClick?: () => void
): Highcharts.SeriesAreasplineOptions => {
  const seriesOptions: Highcharts.SeriesAreasplineOptions = {
    type: 'areaspline',
    id,
    name: id,
    zIndex,
    cursor,
    lineColor,
    fillColor: areaColor,
    lineWidth: enabled ? SERIES_LINE_WIDTH : BG_SERIES_LINE_WIDTH,
    enableMouseTracking: true,
    trackByArea: true,
    stickyTracking: false,
    marker: {
      states: {
        hover: {
          lineColor,
          enabled: enabled ? true : false,
          lineWidth: MARKER_LINE_WIDTH
        }
      }
    },

    data: timeline.map<[number, number]>((timestamp, index) => {
      return [timestamp, values[index]]
    }),
    zones: processZeroValueZones
      ? calculateSeriesZones(values, timeline, lineColor, areaColor)
      : undefined,
    zoneAxis: 'x',

    states: {
      hover: {
        lineWidth: enabled
          ? SERIES_LINE_WIDTH_ON_HOVER
          : BG_SERIES_LINE_WIDTH_ON_HOVER,
        halo: {
          size: 0
        }
      }
    }
  }

  return addClickEventToChartSeries(seriesOptions, onClick)
}

export const calculateSeriesZones = (
  values: number[],
  timeline: number[],
  lineColor:
    | Highcharts.ColorString
    | Highcharts.GradientColorObject
    | Highcharts.PatternObject,
  areaColor: any
): Highcharts.SeriesZonesOptionsObject[] | undefined => {
  /* An array defining zones within a series. The zone definitions have to be in ascending order regarding to the value.*/
  const zones: Highcharts.SeriesZonesOptionsObject[] = []

  /* start calculating consecutive ranges for filling the zones array */
  /* consecutive ZERO values zones in the series are gonna be colored with a subtle disabled color */
  /* consecutive NON-ZERO values zones in the series are gonna be colored with the series color */

  for (let index = 0; index < values.length; index++) {
    if (values[index] === 0) {
      // looks for consecutive ZERO values
      while (index + 1 < values.length && values[index + 1] === 0) {
        index++
      }
      zones.push({
        color: deprecatedTheme.secondaryColors.chartDisabledLine,
        value: timeline[index]
      })
    } else {
      // looks for consecutive NON-ZERO values
      while (index + 1 < values.length && values[index + 1] !== 0) {
        index++
      }

      // if the next value is ZERO we should include this as the index in order to prevent getting the disabled color between this point and the next one, see CONTRAST-35039
      if (index + 1 < values.length && values[index + 1] === 0) {
        index++
      }

      zones.push({
        value: timeline[index],
        color: lineColor,
        fillColor: areaColor
      })
    }
  }

  return zones.length > 0 ? zones : undefined
}

export const getChartTooltipSeriesRowTpl = (
  color: string,
  label: string,
  percentage?: number,
  minWidth?: number
) => {
  const rowMinWidth = !!minWidth ? minWidth : 130

  const squareSize = 13
  const coloredSquareCss = `background-color: ${color}; width: ${squareSize}px; height: ${squareSize}px; display: inline-block;`
  const labelCss = `color: ${theme.color.neutral10}; margin-left: 8px;`

  const percentageCss = `color: ${theme.color.neutral40}; float: right;`
  const rootContainerCss = `min-width: ` + rowMinWidth + `px;`

  const percentageContent = !!percentage
    ? '<span style="' + percentageCss + '">' + percentage + '%' + '</span>'
    : ''

  return (
    '<div style="' +
    rootContainerCss +
    '">' +
    '<div style="' +
    coloredSquareCss +
    '" ></div>' +
    '<span style="' +
    labelCss +
    '">' +
    label +
    '</span>' +
    percentageContent +
    '</div>'
  )
}

export const getChartTooltipHeaderTpl = (title: string, subtitle: string) => {
  const subtitleCss = `color: ${theme.color.neutral40};`

  let tooltipTpl =
    '<div style="' + chartTooltipHeaderTitleCss + '" >' + title + '</div>'
  tooltipTpl += '<div style="' + subtitleCss + '" >' + subtitle + '</div>'

  return tooltipTpl
}

export const getChartPointBySeriesID = (id: string, points) => {
  return points.find((point) => point.series.options.id === id).point
}
